/**
 * Marks Judge.me review-form required fields with a visible "*".
 * Classic form labels omit asterisks; the write-review modal uses "(Required)" text.
 * Normalizes both to a contact-form-style asterisk marker.
 */
(() => {
  if (window.__pjJdgmRequiredMarks) return;
  window.__pjJdgmRequiredMarks = true;

  const MARK_CLASS = 'pj-jdgm-required-mark';
  const ROOT_SELECTOR = [
    '.judgeme-reviews',
    '#judgeme_product_reviews',
    '.jdgm-widget',
    '.jdgm-form',
    '.jdgm-form-dynamic',
    '.jdgm-write-review-modal',
  ].join(',');

  /**
   * @param {string} value
   * @returns {string}
   */
  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  /**
   * @returns {RegExp}
   */
  function requiredTextPattern() {
    const text = window.jdgmSettings?.write_review_flow_required_text;
    if (!text || typeof text !== 'string') {
      return /\s*\(\s*required\s*\)\s*$/i;
    }
    return new RegExp(`\\s*\\(\\s*${escapeRegExp(text.trim())}\\s*\\)\\s*$`, 'i');
  }

  /**
   * @param {Element | null | undefined} label
   */
  function ensureMark(label) {
    if (!(label instanceof HTMLElement)) return;
    if (label.querySelector(`.${MARK_CLASS}, .jdgm-cf__required-sym`)) return;

    const pattern = requiredTextPattern();
    for (const node of [...label.childNodes]) {
      if (node.nodeType !== Node.TEXT_NODE || !node.nodeValue) continue;
      if (!pattern.test(node.nodeValue)) continue;
      node.nodeValue = node.nodeValue.replace(pattern, '').replace(/\s+$/, '');
    }

    const mark = document.createElement('span');
    mark.className = MARK_CLASS;
    mark.setAttribute('aria-hidden', 'true');
    mark.textContent = '*';
    label.appendChild(mark);
  }

  /**
   * @param {ParentNode} [scope]
   */
  function scan(scope = document) {
    const roots =
      scope instanceof Element && scope.matches?.(ROOT_SELECTOR)
        ? [scope, ...scope.querySelectorAll(ROOT_SELECTOR)]
        : [...scope.querySelectorAll(ROOT_SELECTOR)];

    if (!roots.length && scope === document) return;

    const searchRoots = roots.length ? roots : [scope];

    for (const root of searchRoots) {
      root
        .querySelectorAll(
          [
            '.jdgm-form__rating-fieldset > label',
            '.jdgm-form-dynamic__rating-fieldset > label',
            '.jdgm-write-review-modal__field-group--stars .jdgm-write-review-modal__field-label',
            '.jdgm-form__fieldset:has([required]) > label',
            '.jdgm-form__fieldset:has([required]) > .jdgm-form__inline-label',
            '.jdgm-form-dynamic__row:has([required]) > label',
            '.jdgm-write-review-modal__field-group:has([required]) > .jdgm-write-review-modal__field-label',
            '.jdgm-write-review-modal__field-group:has([aria-required="true"]) > .jdgm-write-review-modal__field-label',
          ].join(',')
        )
        .forEach(ensureMark);

      root.querySelectorAll('[required], [aria-required="true"]').forEach((input) => {
        if (!(input instanceof HTMLElement)) return;
        if (!input.closest(ROOT_SELECTOR)) return;

        const group = input.closest(
          '.jdgm-form__fieldset, .jdgm-form-dynamic__row, .jdgm-form-dynamic__name-fieldset, .jdgm-form-dynamic__email-fieldset, .jdgm-write-review-modal__field-group'
        );
        /** @type {Element | null | undefined} */
        let label =
          group?.querySelector(
            `:scope > label, :scope > .jdgm-form__inline-label, :scope > .jdgm-write-review-modal__field-label`
          ) || null;

        if (!label && input.id) {
          const escape =
            typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
              ? CSS.escape
              : (value) => value.replace(/"/g, '\\"');
          label = document.querySelector(`label[for="${escape(input.id)}"]`);
        }

        ensureMark(label);
      });
    }
  }

  const observer = new MutationObserver((mutations) => {
    let shouldScan = false;
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') {
        const host = mutation.target.parentElement?.closest(ROOT_SELECTOR);
        if (host) {
          shouldScan = true;
          break;
        }
        continue;
      }

      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = /** @type {Element} */ (node);
        if (el.matches?.(ROOT_SELECTOR) || el.querySelector?.(ROOT_SELECTOR) || el.closest?.(ROOT_SELECTOR)) {
          shouldScan = true;
          break;
        }
      }
      if (shouldScan) break;
    }

    if (shouldScan) scan();
  });

  const start = () => {
    scan();
    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
